import { z } from 'zod';

import { uuidListParam, boolParam, listQuerySchema, sortDirSchema } from './common.js';
import { videoParamsSchema } from './video.js';
import { videoPlanSchema } from './product-vision.js';
import {
  creativeStatusSchema,
  creativeTypeSchema,
  jobStatusSchema,
  CREDIT_COST_BY_CREATIVE_TYPE,
  type CreativeStatus,
  type CreativeType,
  type JobStatus,
} from './enums.js';
import type { VideoCostBreakdown } from './video.js';
/**
 * ⚠️ TİP-ONLY import ve bu zorunlu: `studio.ts` bu dosyadan `creativeFormat
 * IdSchema`'yı DEĞER olarak alıyor. Buradan oraya bir değer import etseydik
 * çalışma zamanında gerçek bir döngü doğardı; `import type` derlemede
 * siliniyor, yani bağ tek yönlü kalıyor.
 */
import type { StudioCostBreakdown } from './studio.js';
/**
 * ⚠️ Bu import DEĞER taşıyor (şema), tip değil — ve taşıyabiliyor çünkü
 * `studio-models.ts` HİÇBİR ŞEY import etmiyor. Yönü tersine çeviren bir
 * değişiklik (kadronun buradan bir sabit okuması) çalışma zamanında gerçek
 * bir döngü doğurur; gerekçe o dosyanın başlığında.
 */
import { presenterParamsSchema } from './studio-models.js';

/**
 * CREATIVE — üretim stüdyosu, render kuyruğu, varlık kütüphanesi.
 *
 * Tasarım §4.4, §8 (krediler), §9 (render hatası).
 *
 * ---------------------------------------------------------------------------
 * 🔴 Kredi mantığı BURADA YAZILMAZ
 * ---------------------------------------------------------------------------
 * `CreditService.reserve / commit / refund` billing'in. Bu alan onları
 * ÇAĞIRIR. Kendi düşümünü yazan bir kreatif modülü, bir gün billing'den farklı
 * bir bakiye hesaplar ve fark kullanıcının parasında görünür.
 *
 * Yaşam döngüsü:
 *   Creative(DRAFT) + CreativeJob(QUEUED) oluştur
 *   → kredi REZERVE et  ← KUYRUĞA ATMADAN ÖNCE. Rezervasyon başarısızsa iş
 *      hiç açılmaz; sırada bekleyip sonunda "krediniz yok" demek en kötüsü.
 *   → worker RUNNING + progress
 *   → başarı: dosyalar S3'e, status=READY, kredi COMMIT, bildirim
 *   → hata/timeout/iptal: status=FAILED, kredi REFUND (idempotent,
 *      `CreativeJob.refundEntryId` çift iadeyi engeller), bildirim
 */

// ═══════════════════════════════════════════════════════════════════════════
// Üretim tipi kataloğu
// ═══════════════════════════════════════════════════════════════════════════

/**
 * KS1/KS2 — kart ızgarasının kaynağı. Maliyet `enums.ts`'teki
 * `CREDIT_COST_BY_CREATIVE_TYPE`'tan gelir; burada İKİNCİ BİR SAYI YOK.
 *
 * `available` sağlayıcı durumundan hesaplanır: görsel ve video için `FAL_KEY`,
 * metin için `ANTHROPIC_API_KEY`. Arayüz yapılandırılmamış tipi
 * kendiliğinden "yakında" gösterir.
 *
 * ⚠️ `credits` VİDEO TİPLERİNDE BİR TABAN: "8 sn 720p için 25 kredi".
 * Gerçek tutar süre ve çözünürlükle ölçekleniyor ve `/creatives/estimate`ten
 * geliyor. Kart üzerinde "…krediden başlar" demek zorunda; düz "25 kredi"
 * yazmak 15 saniyelik 1080p bir işte yanlış fiyat göstermek olurdu.
 */
export interface CreativeTypeCard {
  type: CreativeType;
  credits: number;
  available: boolean;
  unavailableReasonKey: string | null;
  /**
   * Girdi alanları — üretim akışı formunu bu besliyor.
   *
   * 🔴 `formats` ile `video` BİRBİRİNİN YERİNE GEÇMEZ. `formats` görsel
   * format seçicisi (`CREATIVE_FORMATS` — beş seçenek, 4:5 ve 600×600 dâhil);
   * `video` ise süre / çözünürlük / oran / ses / seslendirme panelini açıyor
   * ve o panelin oran listesi ÜÇ (`VIDEO_ASPECT_RATIOS`). Video kartında
   * `formats` göstermek, sağlayıcının kabul etmediği bir oranı seçtirmek
   * olurdu.
   */
  inputs: (
    | 'brief'
    | 'sourceImage'
    | 'sourceCreative'
    | 'sourceCampaign'
    | 'formats'
    | 'video'
    | 'brandTone'
    | 'brandColor'
  )[];
  output: 'image' | 'video' | 'text';
  labelKey: string;
  descriptionKey: string;
}

export const CREATIVE_CREDIT_COST = CREDIT_COST_BY_CREATIVE_TYPE;

/// UR3 / KT8 — platform format seti. Resize bu listeden üretir.
export const CREATIVE_FORMATS = [
  { id: 'feed_square', ratio: '1:1', width: 1080, height: 1080 },
  { id: 'feed_portrait', ratio: '4:5', width: 1080, height: 1350 },
  { id: 'story', ratio: '9:16', width: 1080, height: 1920 },
  { id: 'landscape', ratio: '16:9', width: 1920, height: 1080 },
  { id: 'square_small', ratio: '1:1', width: 600, height: 600 },
] as const;
export type CreativeFormatId = (typeof CREATIVE_FORMATS)[number]['id'];
export const creativeFormatIdSchema = z.enum([
  'feed_square',
  'feed_portrait',
  'story',
  'landscape',
  'square_small',
]);

// ═══════════════════════════════════════════════════════════════════════════
// Üretim isteği
// ═══════════════════════════════════════════════════════════════════════════

/**
 * UR4 — TOPLAM MALİYET = birim × varyant sayısı ve ÜRETİMDEN ÖNCE hesaplanıp
 * yanıtta döner; rezervasyon bu tutar üzerinden yapılır. Kullanıcı "4 kredi"
 * yazan bir karta basıp 16 kredi harcadığını sonradan öğrenmemeli.
 */
export const MAX_VARIANTS = 4;

/**
 * Brief metninin üst sınırı.
 *
 * 🔴 2000 İDİ VE ÇOK KISAYDI — 19 Ağu 2026'da ölçülerek yükseltildi.
 *
 * O gün sahne şablonları çözülmüş reçetelerini (2.334–3.000 karakter) bu alana
 * basıyordu ve ürünün kendi hazır şablonu, ürünün kendi şemasına takılıyordu:
 * fiyat sorgusu (brief taşımıyor) 200 dönüyor, düğme açık kalıyor, üretim isteği
 * 400 alıyordu.
 *
 * ⚠️ AYNI GÜN İÇİNDE o gerekçe ORTADAN KALKTI: reçeteler sunucuya taşındı ve
 * brief'e artık basılmıyorlar (`sceneParamsSchema` başlığı). Sınır yine de
 * 6000'de bırakıldı — indirmenin bir kazancı yok, düşürmenin ise elle uzun
 * brief yazan kullanıcıyı sessizce kırpma riski var. Sınırın varlık sebebi hâlâ
 * geçerli: sağlayıcıya sınırsız metin göndermek ve arama indeksini şişirmek
 * istemiyoruz.
 *
 * TEK YERDE: `creativeGenerateSchema`, stüdyo uçlarının şemaları ve arayüzün
 * sayaç kutusu bunu okuyor. Beş ayrı `2000` yazılıydı ve biri yükseltilip
 * diğerleri kalsaydı hata yalnızca yer değiştirirdi.
 */
export const CREATIVE_BRIEF_MAX = 6000;

/**
 * Sihirbazın son adımındaki "ek not" alanının üst sınırı.
 *
 * Ayrı bir sabit ve `CREATIVE_BRIEF_MAX`tan çok küçük: not reçetenin SONUNA
 * ekleniyor, yani onunla yarışmıyor ona ekleniyor. 3.000 karakterlik bir
 * reçetenin ardına 6.000 karakterlik bir not koymak, şablonun kurduğu her şeyi
 * ezen ikinci bir brief yazmak olurdu — kullanıcı da neden şablonun düzenini
 * almadığını anlayamazdı.
 */
export const SCENE_NOTE_MAX = 400;

/**
 * SAHNE ŞABLONU İSTEĞİ — reçetenin kendisi DEĞİL, ona giden adres.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 19 Ağu 2026 — brief artık ARAYÜZDE KURULMUYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu şemadan önce sahne şablonları istemcide çözülüyor ve 3.000 karakterlik
 * çözülmüş reçete `brief` alanıyla geri gönderiliyordu. Bunun iki sonucu vardı
 * ve ikincisi ürünün kendisiyle ilgili:
 *
 *   1. Reçetelerin tamamı `apps/web` bundle'ındaydı — kaynağı açan biri altı
 *      şablonu kopyalayıp başka bir araçta kullanabiliyordu.
 *   2. Çözülmüş reçete `Creative.inputParams.brief`e YAZILIYORDU, yani
 *      `GET /creatives/:id` yanıtı da onu geri veriyordu. Bundle kapatılsa
 *      bile tek bir üretim, reçeteyi API üzerinden dışarı çıkarmaya yetiyordu.
 *
 * Artık istemci yalnız KİMLİK ve KULLANICI METNİ gönderiyor; reçete
 * `@mentorivo/shared/scene-templates` içinde ve yalnız worker'da, render
 * anında çözülüyor. `brief` alanı bu yolda kullanıcının kendi notunu taşıyor.
 *
 * ⚠️ `templateId` burada bir ENUM DEĞİL, serbest dize. Katalog koddan geliyor
 * ve şemaya kimlik listesi gömmek, bir şablon eklendiğinde `packages/shared`in
 * yeniden derlenmesini ZORUNLU kılardı. Doğrulama serviste: bilinmeyen kimlik
 * `SCENE_TEMPLATE_NOT_FOUND` ile reddediliyor.
 */
export const sceneParamsSchema = z.object({
  templateId: z.string().trim().min(1).max(64),
  /// `SCENE_PALETTES` kimliği. Bilinmeyen değer sunucuda `auto`ya düşüyor.
  paletteId: z.string().trim().max(32).default('auto'),
  /// BOŞ bırakılan yuvaların hangi dilde yazılacağı — dolu yuvalar aynen basılır.
  copyLanguage: z.enum(['tr', 'en']).default('tr'),
  /**
   * "AI'ye bırak" anahtarı.
   *
   * `true` iken `text` YOK SAYILIYOR ve bu bilinçli: anahtarı açan kullanıcının
   * daha önce yazdığı metinler formda duruyor (kapatınca geri gelsinler diye)
   * ve onları sunucuya göndermemek istemci ile sunucu arasında ikinci bir
   * "hangisi kazanır" kuralı doğururdu. Tek kural burada: bayrak açıksa metin
   * yok.
   */
  aiCopy: z.boolean().default(false),
  /**
   * METİNSİZ ÜRET — sahne kurulur, tipografi HİÇ basılmaz (20 Ağu 2026).
   *
   * `aiCopy`in "daha fazlası" DEĞİL, üçüncü bir cevap. Üç kip birbirini dışlar
   * ve sıralaması şu: metni ben yazayım → AI yazsın → hiç metin olmasın.
   *
   * 🔴 `aiCopy` ile birlikte gelirse BU kazanıyor ve sunucu `aiCopy`i
   * `false`a çekip saklıyor (`resolveScene`). Sebep `aiCopy`in kendi
   * başlığındakiyle aynı: çelişebilen iki bayrağı olduğu gibi saklamak, altı
   * ay sonra o satıra bakan birine hangisinin uygulandığını sordurur.
   *
   * Reçete tarafındaki karşılığı bir bayrak değil bir DEĞİŞ TOKUŞ:
   * `{{typography}}` bloğu düşüyor, yerine `NO_TEXT_RULES` geçiyor
   * (`scene-templates.ts` → `NO_TEXT_RULES` başlığı).
   */
  textless: z.boolean().default(false),
  /**
   * Yuva anahtarı → kullanıcının yazdığı metin.
   *
   * Anahtar kümesi ŞABLONA ait ve serviste doğrulanıyor: şablonda olmayan bir
   * anahtar sessizce düşürülüyor, çünkü reçetede karşılığı olmayan bir metin
   * hiçbir yere basılamaz. Değer sınırı yuva başına (`slot.max`) yine serviste.
   */
  text: z.record(z.string().max(64), z.string().max(300)).default({}),
  /// Sihirbazın son adımındaki serbest not — reçetenin SONUNA ekleniyor.
  note: z.string().trim().max(SCENE_NOTE_MAX).optional(),
});
export type SceneParams = z.infer<typeof sceneParamsSchema>;

export const creativeGenerateSchema = z
  .object({
    type: creativeTypeSchema,
    variants: z.number().int().min(1).max(MAX_VARIANTS).default(1),
    /// UR1 — brief metni. Kütüphane araması (KT3) bunun içinde arıyor.
    brief: z.string().trim().max(CREATIVE_BRIEF_MAX).optional(),
    /// Yüklenmiş varlığın S3 anahtarı (upload ucundan dönen).
    sourceAssetKey: z.string().max(400).optional(),
    /**
     * KATALOG ÜRÜNÜ — kaynak görselin ikinci yolu (19 Ağu 2026).
     *
     * ═══════════════════════════════════════════════════════════════════════
     * Neden bir anahtar değil bir KİMLİK
     * ═══════════════════════════════════════════════════════════════════════
     * Bağlı mağazadaki ürünün fotoğrafı bizim depomuzda DEĞİL, platformun
     * CDN'inde. Arayüzün o adresi indirip yeniden yüklemesi mümkün değil:
     * mağaza CDN'lerinin çoğu tarayıcıdan çapraz kaynak okumaya izin vermiyor
     * (Trendyol ve Hepsiburada'da ölçüldü) ve izin verse bile aynı dosyayı
     * kullanıcının bağlantısı üzerinden bir gidip bir gelmesi gerekirdi.
     *
     * Kimlik gönderiliyor, adres SUNUCUDA çözülüyor ve üretim anına
     * DONDURULUYOR (`StudioProductSnapshot`): ürün mağazadan silinse bile
     * "bu görsel hangi üründen üretildi" sorusu altı ay sonra da cevaplanıyor.
     * Banner ve deneme stüdyoları aynı yolu zaten kullanıyordu; bu alan onu
     * serbest kipe (sahne şablonları) açıyor.
     *
     * 🔴 `sourceAssetKey` ile BİRLİKTE gönderilemez — aşağıdaki `refine`.
     * İkisi de tabanı (`image_urls[0]`) belirliyor ve sessizce birini seçmek,
     * kullanıcının yüklediği fotoğrafı ya da seçtiği ürünü haber vermeden yok
     * saymak olurdu.
     */
    productId: z.uuid().optional(),
    /**
     * MARKA LOGOSU — ikinci girdi görseli (19 Ağu 2026).
     *
     * ═══════════════════════════════════════════════════════════════════════
     * 🔴 `sourceAssetKey` OLMADAN ANLAMSIZ — ve bu teknik bir kısıt
     * ═══════════════════════════════════════════════════════════════════════
     * Sağlayıcının düzenleme ucu görselleri SIRALI alıyor: `image_urls[0]`
     * dönüştürülecek taban, sonrakiler referans. Logo tek başına gönderilirse
     * taban olur ve model sahneyi LOGODAN türetir — kullanıcı ürününü değil
     * logosunun büyütülmüş hâlini alır.
     *
     * Aynı sözleşme banner stüdyosunda da geçerli ve orada da aynı gerekçeyle
     * yazılı (`fal-studio.provider.ts` → "GÖRSEL SIRASI SÖZLEŞME"). Burada
     * şema düzeyinde zorlanıyor ki arayüz unutursa istek 400 alsın, sessizce
     * yanlış bir görsel üretilmesin.
     */
    logoAssetKey: z.string().max(400).optional(),
    /// IMPROVED / RESIZED için kaynak kreatif.
    sourceCreativeId: z.uuid().optional(),
    /// UR2 — Improve akışında metrik + görsel BİRLİKTE analiz edilir.
    sourceCampaignId: z.uuid().optional(),
    formats: z.array(creativeFormatIdSchema).max(5).optional(),
    brandTone: z.string().trim().max(200).optional(),
    brandColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    /// UR10 — yeniden üretimde AÇIK ONAY. Kredi tekrar yanacak.
    confirmRecharge: z.boolean().default(false),
    /**
     * VİDEO AYARLARI — Genişleme Adım 7. Yalnız PRODUCT_VIDEO / UGC_VIDEO'da
     * anlamlı.
     *
     * `formats` alanının yerine GEÇMİYOR: orası görsel formatı (4:5 ve 600×600
     * dâhil beş seçenek), burası video ekseni (üç oran + süre + çözünürlük +
     * ses). Aynı alanı iki anlamda kullanmak, video işinde `feed_portrait`
     * gelen bir isteği sessizce en yakın orana yuvarlamak olurdu.
     *
     * 🔴 Süre ve çözünürlük KREDİYİ DEĞİŞTİRİYOR — `creative.cost.ts`
     * (`videoCreditCost`). Bu yüzden üretimden önce `POST /creatives/estimate`
     * aynı nesneyi alıyor ve gerçek tutarı döndürüyor.
     */
    video: videoParamsSchema.optional(),
    /**
     * SAHNE ŞABLONU — görsel `prompt` kipinin hazır reklam postu (19 Ağu 2026).
     *
     * Gövdede `brief` ile BİRLİKTE gelebiliyor ve çakışmıyorlar: reçete
     * şablondan, kullanıcının ek notu `scene.note`tan geliyor. Kök `brief`
     * bu yolda kullanılmıyor — arayüz onu göndermiyor, gönderse de worker
     * şablon varken reçeteyi tercih ediyor (`render.service.ts`).
     */
    scene: sceneParamsSchema.optional(),
    /**
     * ÜRÜNE ÖZEL VİDEO PLANI — görü analizinden çıkan JSON prompt
     * (20 Ağu 2026).
     *
     * ═══════════════════════════════════════════════════════════════════════
     * 🔴 Neden İSTEMCİDEN geliyor — `scene` ile taban tabana zıt karar
     * ═══════════════════════════════════════════════════════════════════════
     * `scene` bir ADRES taşıyor (şablon kimliği + yuvalar) ve reçete sunucuda
     * kalıyor; oradaki reçeteler BİZİM varlığımız. Plan ise kullanıcının
     * ürününe özel üretildi ve kullanıcı onu EKRANDA DÜZENLİYOR (ürün kararı,
     * 20 Ağu 2026). Düzenlenmiş bir metnin sunucuda kalan bir kopyayla
     * değiştirilmesi, kullanıcının yaptığı düzeltmeyi sessizce çöpe atmak
     * olurdu.
     *
     * Bedeli: prompt'a giren her alan artık kullanıcı girdisi. Savunma
     * `product-vision.ts` başlığında üç madde hâlinde yazılı ve ilki bu şema
     * — her alan uzunluk ve sayı olarak sınırlı.
     *
     * ⚠️ `brief` ile ÇAKIŞMIYOR, ONU EZİYOR: plan varken worker prompt'u
     * plandan derliyor (`fal-video.provider.ts`). Kullanıcının serbest metni
     * plana zaten analiz aşamasında `note` olarak girdi.
     *
     * Yalnız PRODUCT_VIDEO'da anlamlı. UGC videosunun prompt'u konuşma
     * metninden kuruluyor ve plan orada bir karşılık bulmuyor.
     */
    videoPlan: videoPlanSchema.optional(),
    /**
     * Planın çıktığı analiz — İZLENEBİLİRLİK ve SÜREKLİLİK.
     *
     * İki işi var:
     *   1. "Bu video hangi analizden çıktı" sorusu altı ay sonra da
     *      cevaplanıyor.
     *   2. Worker o satırdan ÜRÜN KÜNYESİNİ okuyup prompt'un süreklilik
     *      bloğunu zenginleştiriyor (marka işareti görünüyor mu, ürünün
     *      renkleri neler, kaynak fotoğrafın ışık yönü). Bu bilgiler planın
     *      kendisinde yok ve `inputParams`a kopyalanmadılar — kopya, analiz
     *      düzeltilse bile eski hâliyle donardı.
     *
     * Satır bulunamazsa (silinmiş, başka kiracıya ait) üretim DURMUYOR: plan
     * tek başına derleniyor. Zenginleştirme bir iyileştirme, bir şart değil.
     */
    visionAnalysisId: z.uuid().optional(),
    /**
     * MANKEN — ürünü tanıtan kişi (21 Ağu 2026).
     *
     * ═══════════════════════════════════════════════════════════════════════
     * 🔴 Neden stüdyo modu DEĞİL de kök alan
     * ═══════════════════════════════════════════════════════════════════════
     * Önceki hâlinde manken bir stüdyo moduydu (`studio.mode === 'tryon'`) ve
     * kendi sekmesi, kendi ucu, kendi fiyatı vardı. Bu, kullanıcıya cevabı
     * olmayan bir soru sorduruyordu: "sahne şablonu mu kullanayım, manken mi".
     * İkisi rakip değil — bir manken bir sahnede durur.
     *
     * Kök alan olması tam olarak bunu mümkün kılıyor: `prompt` sekmesinde
     * serbest brief'le, `product` sekmesinde katalog ürünü ve sahne
     * şablonuyla BİRLİKTE gönderilebiliyor. Üretim yolu değişmiyor, yalnız
     * `image_urls` dizisine ikinci bir referans giriyor.
     *
     * 🔴 `logoAssetKey`in aksine TEK BAŞINA gönderilebilir. Logo tek başına
     * gelirse taban olur ve sahne logodan türer (o alanın başlığındaki
     * gerekçe); manken tek başına geldiğinde taban OLMASI zaten istenen şey —
     * kişi sahneye giriyor, brief de ortamı anlatıyor. Ürün görseli varsa
     * taban ürün olur, manken ikinci referansa düşer; sıra sağlayıcıda
     * kurulu (`fal.provider.ts` → `image_urls`).
     *
     * Video tarafında karşılığı YOK: sağlayıcı video ucu tek referans görsel
     * alıyor ve o slot zaten ürünün.
     */
    presenter: presenterParamsSchema.optional(),
  })
  /**
   * Logo TEK BAŞINA gönderilemez — gerekçe `logoAssetKey` başlığında
   * (sağlayıcının `image_urls[0]` tabanı logoya kayar).
   *
   * `sourceCreativeId` de kabul ediliyor çünkü improve/resize akışında taban
   * görsel kaynak kreatifin çıktısı: `sourceAssetKey` boş ama taban VAR.
   */
  .refine(
    (input) =>
      !input.logoAssetKey ||
      Boolean(input.sourceAssetKey || input.sourceCreativeId || input.productId),
    {
      message: 'logoAssetKey yalnızca bir kaynak görselle birlikte gönderilebilir.',
      path: ['logoAssetKey'],
    },
  )
  /**
   * Taban görsel TEK KAYNAKTAN. Kural banner ve deneme şemalarındakiyle aynı
   * (`creative.schemas.ts` → "productId ve sourceAssetKey aynı anda
   * verilemez") ve aynı gerekçeyle: iki taban gönderildiğinde hangisinin
   * kazandığı ancak çıktıya bakarak anlaşılırdı.
   */
  .refine((input) => !(input.productId && input.sourceAssetKey), {
    message: 'productId ve sourceAssetKey aynı anda verilemez.',
    path: ['productId'],
  });
export type CreativeGenerateInput = z.infer<typeof creativeGenerateSchema>;

/// Üretim başlamadan maliyeti sorma ucu — form canlı hesaplasın.
export interface CreativeCostEstimate {
  type: CreativeType;
  variants: number;
  unitCredits: number;
  totalCredits: number;
  available: number;
  sufficient: boolean;
  /// Yetmiyorsa arayüz paket satın almaya yönlendirir.
  topupPath: string | null;
  /**
   * Video maliyetinin DÖKÜMÜ — yalnız video tiplerinde dolu.
   *
   * 🔴 Sadece toplam dönmüyor. Kullanıcı "neden 71 kredi" diye sorduğunda
   * cevabın arayüzde olması gerekiyor ve o cevabı arayüzün YENİDEN
   * HESAPLAMASI, ikinci bir fiyat mantığı yazmak demekti. Çarpanlar da
   * sunucudan geliyor.
   */
  video: VideoCostBreakdown | null;
  /**
   * Görsel stüdyo maliyetinin DÖKÜMÜ — yalnız stüdyo modlarında dolu
   * (Genişleme Adım 8).
   *
   * `video` ile aynı gerekçe ve aynı şekilde AYRI: bir üretim ikisini birden
   * taşıyamaz (stüdyo modlarının üçü de görsel tipi üretiyor) ve tek bir
   * `breakdown` alanında birleştirmek, hangi eksenin okunacağını arayüzün
   * kreatif tipinden çıkarmasını gerektirirdi.
   */
  studio: StudioCostBreakdown | null;
}

export interface CreativeGenerateResponse {
  creativeIds: string[];
  jobIds: string[];
  reservedCredits: number;
  reservationId: string;
  estimatedSeconds: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// İş takibi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * UR6 / KS4 — video DAKİKALAR sürebilir. Kullanıcı sayfadan ayrılabilmeli,
 * iş arka planda sürmeli, bitince bildirim gelmeli. İlerleme POLLING ile
 * okunur (WS bu turda yok).
 */
export interface CreativeJobRow {
  id: string;
  creativeId: string;
  status: JobStatus;
  /// 0–100.
  progress: number;
  provider: string | null;
  attempts: number;
  error: string | null;
  reservedCredits: number;
  /// İade edildiyse defter satırının kimliği — çift iade koruması.
  refundEntryId: string | null;
  /**
   * BU İŞİN TEK BAŞINA beklenen süresi (saniye) — kuyruk sayacının kaynağı.
   *
   * 🔴 `CreativeGenerateResponse.estimatedSeconds` ile AYNI ŞEY DEĞİL: o sayı
   * bir PARTİNİN toplam beklemesi (varyantlar paralel üretildiği için
   * doğrusalın altında) ve yalnız üretimi başlatan isteğe dönüyor. Bu alan
   * satır başına, yani kuyruktaki her iş kendi geri sayımını taşıyor.
   *
   * Sunucudan geliyor çünkü hesap girdilere bağlı: video süresi, çözünürlük,
   * stüdyo kipi. Arayüz bunları görmüyor (`inputParams` liste yanıtında yok)
   * ve görseydi bile ikinci bir süre mantığı yazmak olurdu.
   *
   * ⚠️ TAHMİN, söz değil. Sağlayıcı aşabilir; arayüz sıfırın altına inince
   * sayı göstermeyi bırakıyor.
   */
  estimatedSeconds: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  /// Kreatifin özeti — ayrı çağrı gerekmesin.
  creative: { type: CreativeType; name: string | null; thumbUrl: string | null };
}

/// Görsel 3 dakika, video 20 dakika. Aşımda iş FAILED + kredi İADE.
export const RENDER_TIMEOUT_SECONDS = { image: 180, video: 1200, text: 60 } as const;

// ═══════════════════════════════════════════════════════════════════════════
// Kütüphane
// ═══════════════════════════════════════════════════════════════════════════

export const creativeListQuerySchema = listQuerySchema.extend({
  type: creativeTypeSchema.optional(),
  status: creativeStatusSchema.optional(),
  /**
   * BAŞARISIZ İŞLERİ GİZLER — üretim geçmişi ekranının varsayılanı.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * Neden istemcide süzülemiyor
   * ═════════════════════════════════════════════════════════════════════════
   * Liste SAYFALANIYOR. Gelen 24 kutudan başarısızları tarayıcıda atmak,
   * sayfanın bazen 24 bazen 19 kutu göstermesi ve "toplam" sayısının hiçbir
   * sayfada tutmaması demekti. Süzme nerede sayılıyorsa orada yapılmalı.
   *
   * ⚠️ AÇIK `status` KAZANIYOR: ikisi birden geldiğinde (`status=FAILED` +
   * `excludeFailed=true`) kullanıcının açıkça seçtiği durum uygulanıyor.
   * Aksi hâlde uç, istenen filtreyi sessizce boş bir listeye çevirirdi.
   */
  excludeFailed: boolParam.optional(),
  /// KT2 — kaynak kampanya. `inputParams.sourceCampaignId` üzerinden.
  sourceCampaignId: z.uuid().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  /// KS6 — otomasyondan gelen işler.
  origin: z.enum(['user', 'workflow', 'agent']).optional(),
  /**
   * BELİRLİ KİMLİKLER — ajanın kreatif seçici kartı için (22 Ağu 2026).
   *
   * ═════════════════════════════════════════════════════════════════════════
   * Neden gerekti: imzalı adres KARTA GÖMÜLEMEZ
   * ═════════════════════════════════════════════════════════════════════════
   * Sohbet kartları KALICI — sayfa yenilenince satırdan geri geliyorlar. Ama
   * `CreativeRow.thumbUrl` kısa ömürlü bir imzalı adres; karta yazılsaydı iki
   * saat sonra sessizce kırık bir görsel olurdu.
   *
   * Bu yüzden kart yalnız KİMLİK taşıyor ve arayüz önizlemeleri çizim anında
   * bu süzgeçle tazeliyor. Tek istek: kimlik başına `GET /creatives/:id`
   * çağırmak, üç kreatiflik bir kartta üç istek demekti.
   *
   * Sayfalama ile birlikte kullanılabiliyor ama pratikte gerekmiyor: liste
   * zaten seçilenlerle sınırlı.
   */
  ids: uuidListParam.optional(),
  sort: z.enum(['createdAt', 'name', 'type']).default('createdAt'),
  dir: sortDirSchema,
});
export type CreativeListQuery = z.infer<typeof creativeListQuerySchema>;

/**
 * ⚠️ KT3 arama: brief metni `inputParams` JSON'unun içinde. Sözleşme kararı:
 * brief'in ilk 120 karakteri ÜRETİM ANINDA `Creative.name`'e de yazılır ve
 * arama `name` üzerinde `ILIKE` ile yapılır. JSON yolu üzerinde arama
 * indekslenemez ve tablo büyüdükçe sıralı tarama olur.
 */
export interface CreativeRow {
  id: string;
  type: CreativeType;
  status: CreativeStatus;
  name: string | null;
  provider: string | null;
  /// KT1 — imzalı önizleme URL'i. S3 bucket'ı ASLA public değil.
  previewUrl: string | null;
  thumbUrl: string | null;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  creditCost: number;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  /// KS6 — nereden geldi.
  origin: { type: 'user' | 'workflow' | 'agent'; workflowId?: string; runId?: string } | null;
  /// Improve/Resize kaynağı.
  sourceCreativeId: string | null;
  sourceCampaignId: string | null;
}

export interface CreativeDetail extends CreativeRow {
  /// KT4 — üretim girdileri (sır alanı yok, brief ve parametreler).
  inputParams: Record<string, unknown> | null;
  jobs: CreativeJobRow[];
  /// KT6 — hangi reklamlarda yayında.
  publishedAds: { adId: string; adName: string; campaignName: string; platform: string }[];
  /// KT7 — publish sonrası performans. `Ad.creativeId` üzerinden metriklere
  /// bağlanır; hiç yayına girmediyse null.
  performance: {
    spend: number;
    impressions: number;
    clicks: number;
    ctr: number | null;
    roas: number | null;
    currency: string;
  } | null;
  /// İndirme için kısa ömürlü imzalı bağlantı.
  downloadUrl: string | null;
  downloadExpiresAt: string | null;
}

/**
 * KT10 — depolama kullanımı.
 *
 * 🔴 `Creative`'de DOSYA BOYUTU kolonu YOK ve `plans.ts`'te depolama kotası
 * TANIMLI DEĞİL. Bugün yapılabilen: S3'te workspace önekli nesnelerin
 * toplamını periyodik hesaplayıp Redis'te önbelleklemek. `quotaBytes` null
 * dönüyor ve arayüz kota çubuğunu GİZLİYOR — uydurma bir kota göstermek,
 * kullanıcıyı olmayan bir sınıra göre davranmaya iter.
 */
export interface StorageUsageResponse {
  usedBytes: number;
  objectCount: number;
  quotaBytes: number | null;
  calculatedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Yükleme, beğen/at, publish
// ═══════════════════════════════════════════════════════════════════════════

/**
 * UR1 — ürün görseli / marka varlığı yükleme.
 *
 * SVG YASAK: içine script gömülebiliyor ve imzalı URL ile servis edildiğinde
 * kendi alan adımızda çalışır. Allowlist beyaz liste olarak uygulanır, kara
 * liste olarak değil.
 */
export const UPLOAD_MIME_ALLOWLIST = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
] as const;
export const UPLOAD_MAX_BYTES = { image: 10 * 1024 * 1024, video: 200 * 1024 * 1024 } as const;

export const uploadRequestSchema = z.object({
  fileName: z.string().min(1).max(200),
  mimeType: z.enum(UPLOAD_MIME_ALLOWLIST),
  sizeBytes: z.number().int().min(1).max(UPLOAD_MAX_BYTES.video),
});
export type UploadRequestInput = z.infer<typeof uploadRequestSchema>;

export interface UploadTicketResponse {
  /// İmzalı doğrudan yükleme adresi (PUT). API üzerinden akıtmak, 200 MB'lık
  /// videoyu Node sürecinden geçirmek demek.
  uploadUrl: string;
  /// Yükleme bitince üretim isteğinde bu anahtar verilir.
  assetKey: string;
  expiresAt: string;
  /// Sanitize edilmiş dosya adı — kullanıcının verdiği ad kullanılmaz.
  storedFileName: string;
}

/**
 * UR8 — beğen / at. Atılan varyant kütüphaneye GİRMEZ: `Creative` satırı
 * silinir ve S3 nesnesi temizlenir.
 *
 * KREDİ İADE EDİLMEZ — üretim gerçekleşti. Bu kural arayüzde YAZILI olmalı;
 * sürpriz olursa kullanıcı ürüne değil kendine kızmaz.
 */
export const discardSchema = z.object({
  creativeIds: z.array(z.uuid()).min(1).max(20),
});
export type DiscardInput = z.infer<typeof discardSchema>;

/// KT6 — reklama publish. Yazma aksiyonu: onay + audit + MEMBER+ rolü.
export const publishSchema = z.object({
  /// Hedef ad set ya da mevcut reklam.
  targetLevel: z.enum(['ADSET', 'AD']),
  targetId: z.uuid(),
  /// Metin kreatifi için başlık/gövde ezme.
  headline: z.string().trim().max(200).optional(),
  bodyText: z.string().trim().max(1000).optional(),
  ctaType: z.string().trim().max(40).optional(),
  /**
   * 🔴 ZORUNLU — ve isteğe bağlı olmaması `rmfAdCreativeSchema.linkUrl` ile
   * AYNI gerekçe: reklamın hedef adresi bir sunum detayı değil, var oluş
   * amacı.
   *
   * Bu uç o kararın DIŞINDA kalmıştı: alan hiç yoktu, servis connector'a
   * `linkUrl: null` gönderiyordu ve Meta adapter'ı boşluğu
   * `https://mentorivo.com` ile dolduruyordu. Yani kütüphaneden yayınlanan
   * her reklam, kullanıcının müşterisine değil BİZİM sitemize gidiyordu ve
   * bunu hiçbir ekran söylemiyordu.
   */
  linkUrl: z.url().max(2000),
  /**
   * Reklamın yayınlandığı marka kimliği: Meta'da Sayfa kimliği, LinkedIn'de
   * organizasyon URN'i.
   *
   * Boş bırakılırsa adapter hedefin altındaki mevcut bir reklamdan devralmayı
   * dener — mevcut bir ad set'e ikinci kreatif eklerken doğru davranış budur.
   * YENİ açılmış bir ad set'in ilk reklamında devralınacak bir şey yoktur ve
   * bu alan ZORUNLU hâle gelir; verilmezse adapter neyi istediğini söyleyen
   * bir hata döndürür.
   */
  identityRef: z.string().trim().min(1).max(128).optional(),
});
export type PublishInput = z.infer<typeof publishSchema>;

export interface PublishResponse {
  creativeId: string;
  adId: string;
  platformRef: string;
  status: CreativeStatus;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sağlayıcı soyutlaması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `CreativeProvider`: `getCapabilities()`, `generateCopy`, `generateImage`,
 * `improve`, `resize`, `generateVideo`.
 *
 *  - Görsel: `FAL_KEY` .env'de VAR (fal.ai / Flux).
 *  - Metin: Anthropic (Faz 8'de açıldı).
 *  - Video: fal.ai / Kling v3 + AI Avatar (Genişleme Adım 7'de açıldı).
 *    Seçim kullanıcıya soruldu; gerekçe `apps/worker/src/render/provider/
 *    fal-video.provider.ts` başlığında. Üçü de AYNI anahtara (`FAL_KEY`)
 *    bağlı — video için ikinci bir sağlayıcı hesabı yok.
 */
export interface CreativeProviderCapabilities {
  name: string;
  supportedTypes: CreativeType[];
  maxVariants: number;
  configured: boolean;
}

export const CreativeErrorCode = {
  CREATIVE_NOT_FOUND: 'CREATIVE_NOT_FOUND',
  JOB_NOT_FOUND: 'JOB_NOT_FOUND',
  /// Bu tip için sağlayıcı yapılandırılmamış (anahtar eksik).
  PROVIDER_NOT_CONFIGURED: 'PROVIDER_NOT_CONFIGURED',
  /// `details: { required, available }` — billing'den geliyor.
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  /// Grace bitti; üretim kapalı. Kütüphane ve indirme AÇIK kalır —
  /// üretilmiş varlık kullanıcının.
  GENERATION_DISABLED: 'GENERATION_DISABLED',
  /// Improve/Resize kaynağı bulunamadı ya da hazır değil.
  SOURCE_NOT_READY: 'SOURCE_NOT_READY',
  /// Yükleme mime türü ya da boyutu kabul edilmiyor.
  UPLOAD_REJECTED: 'UPLOAD_REJECTED',
  /// Publish hedefi bulunamadı ya da platform reddetti.
  PUBLISH_FAILED: 'PUBLISH_FAILED',
  /// Kreatif zaten yayında; tekrar publish edilmez.
  ALREADY_PUBLISHED: 'ALREADY_PUBLISHED',
  /// Yeniden üretim açık onay ister.
  RECHARGE_CONFIRMATION_REQUIRED: 'RECHARGE_CONFIRMATION_REQUIRED',
  /// İş hâlâ çalışıyor; iptal edilemez ya da silinemez.
  JOB_IN_PROGRESS: 'JOB_IN_PROGRESS',

  // ── Genişleme Adım 8 — görsel stüdyo ────────────────────────────────────
  /**
   * Toplu üretimde istemcinin gönderdiği `confirmCredits` sunucunun hesabıyla
   * TUTMUYOR.
   *
   * `details: { expected, submitted }` — arayüz farkı gösterip yeni tutarla
   * onay isteyebilsin. `RECHARGE_CONFIRMATION_REQUIRED`den ayrı bir kod
   * çünkü sebep farklı: orada onay HİÇ verilmemiş, burada verilen onay
   * ESKİMİŞ (bir ürünün fiyatı ya da uygunluğu tahminden sonra değişti).
   */
  COST_CONFIRMATION_MISMATCH: 'COST_CONFIRMATION_MISMATCH',
  /**
   * Seçilen şablon istenen alanı taşımıyor (ör. `supportsBadge: false` bir
   * şablona rozet metni gönderildi).
   *
   * Sessizce yok saymak da bir seçenekti ve daha kötüydü: kullanıcı rozet
   * yazar, banner'da rozet çıkmaz, hiçbir yerde sebebini göremez.
   */
  TEMPLATE_FIELD_UNSUPPORTED: 'TEMPLATE_FIELD_UNSUPPORTED',
  /**
   * Sahne şablonu kimliği katalogda yok — 19 Ağu 2026.
   *
   * 400 ve SESSİZCE YOK SAYILMIYOR: katalog `/creatives/studio/options`ten
   * geliyor, yani arayüzün gönderdiği her kimlik oradan çıkmış olmalı.
   * Bilinmeyen bir kimlik ya çok eski bir sekme ya elle kurulmuş bir istek
   * demek; ikisinde de şablonsuz üretime düşmek, kullanıcının seçtiği düzeni
   * vermeden kredi harcamak olurdu.
   */
  SCENE_TEMPLATE_NOT_FOUND: 'SCENE_TEMPLATE_NOT_FOUND',
  /// Büyütme kaynağı zaten hedef çözünürlükte ya da üstünde. Kredi harcayıp
  /// aynı görseli geri vermek yerine üretimden ÖNCE reddediliyor.
  ALREADY_AT_TARGET_RESOLUTION: 'ALREADY_AT_TARGET_RESOLUTION',
  /// Katalog ürünü bulunamadı ya da bu çalışma alanına ait değil.
  PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
  /// Ürünün kapak görseli yok ve seçilen şablon ürün fotoğrafı istiyor.
  PRODUCT_IMAGE_MISSING: 'PRODUCT_IMAGE_MISSING',

  // ── Şema borç turu — kreatif onayı ──────────────────────────────────────
  /**
   * YAYIN KAPISI. Çalışma alanı onay istiyor ve bu kreatif onaylı değil.
   *
   * `details: { approvalStatus }` — arayüz duruma göre farklı davranıyor:
   * `NOT_REQUIRED`/`REJECTED` ise "onaya gönder" düğmesi, `PENDING` ise
   * "onay bekleniyor" şeridi. Tek kod ama üç farklı ekran.
   *
   * `SOURCE_NOT_READY`den AYRI ve bilerek: orada eksik olan DOSYA, burada
   * eksik olan İNSANIN kararı. Tek koda bindirmek kullanıcıya "bekle,
   * üretim sürüyor" dedirtirdi — oysa üretim bitmiş, onaycı bekleniyor.
   */
  APPROVAL_REQUIRED: 'APPROVAL_REQUIRED',
  /// Kreatif zaten onay kuyruğunda ve talep hâlâ geçerli. Süresi geçmiş bir
  /// talep bu hatayı VERMEZ — yerine yenisi açılır (gerekçe serviste).
  APPROVAL_ALREADY_PENDING: 'APPROVAL_ALREADY_PENDING',

  // ── Ürün görseli analizi — 20 Ağu 2026 ──────────────────────────────────
  /// Analiz kaydı bulunamadı ya da bu çalışma alanına ait değil.
  VISION_ANALYSIS_NOT_FOUND: 'VISION_ANALYSIS_NOT_FOUND',
  /**
   * Analiz hâlâ kuyrukta ya da koşuyor; aynı kaynak için ikinci bir analiz
   * AÇILMIYOR.
   *
   * Sessizce ikinci bir iş açmak da bir seçenekti ve daha pahalıydı: arayüz
   * yükleme sonrası analizi kendiliğinden tetikliyor ve iki sekmede açık bir
   * panel (ya da hızlı bir yeniden yükleme) aynı fotoğraf için iki kez 2
   * kredi yakardı. `details: { analysisId }` — arayüz o kaydı yoklamaya
   * devam ediyor.
   */
  VISION_ANALYSIS_IN_PROGRESS: 'VISION_ANALYSIS_IN_PROGRESS',

  // ── Manken kadrosu — 21 Ağu 2026 ────────────────────────────────────────
  /**
   * `presenter.modelId` ne hazır kadroda ne de kiracının kendi mankenlerinde.
   *
   * 400 ve SESSİZCE MANKENSİZ ÜRETİME DÜŞMÜYOR — `SCENE_TEMPLATE_NOT_FOUND`
   * ile aynı gerekçe: kullanıcı bir kişi seçtiyse çıktıda o kişiyi bekliyor.
   * Kimliği yok sayıp üretmek, seçilen şeyi vermeden kredi harcamak olurdu.
   */
  STUDIO_MODEL_NOT_FOUND: 'STUDIO_MODEL_NOT_FOUND',
  /**
   * Manken seçildi ama referans fotoğrafı HENÜZ YOK (`state !== 'READY'`).
   *
   * `STUDIO_MODEL_NOT_FOUND`tan ayrı bir kod çünkü kullanıcının yapacağı şey
   * farklı: orada başka birini seçmesi gerekiyor, burada yalnız beklemesi.
   * `details: { state }` — arayüz "üretiliyor" ile "başarısız oldu"yu
   * ayırabilsin.
   */
  STUDIO_MODEL_NOT_READY: 'STUDIO_MODEL_NOT_READY',
  /**
   * Kiracı `STUDIO_MODEL_MAX_PER_WORKSPACE` sınırına ulaştı.
   *
   * Kredi zaten bir fren ama tek fren o olsaydı bütçesi geniş bir kiracı
   * kendi kullanamayacağı bir kadro kurardı — kadro bir SEÇİM ekranı ve
   * otuz kişilik listede seçim yapmak zor.
   */
  STUDIO_MODEL_LIMIT_REACHED: 'STUDIO_MODEL_LIMIT_REACHED',
} as const;
export type CreativeErrorCode = (typeof CreativeErrorCode)[keyof typeof CreativeErrorCode];

export const creativeJobListQuerySchema = listQuerySchema.extend({
  status: jobStatusSchema.optional(),
  /// Yalnız devam edenler — KS4 kartı.
  activeOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// KREATİF ANALİZ EKRANI — Faz 6
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Reklam düzeyi kreatif teşhisi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bu ekran BİZİM ÜRETTİĞİMİZLE SINIRLI DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * `GET /campaigns/:id/creatives` yalnız `Ad.creativeId` dolu olanları, yani
 * bizim stüdyoda üretilenleri döndürüyor. Bir kullanıcının reklamlarının
 * çoğu platformda, bizden önce oluşturulmuş — ve "hangi kreatifim yoruldu"
 * sorusunun cevabı tam olarak orada.
 *
 * Bu yüzden analiz TÜM reklamları listeliyor; `creative` alanı yalnız bizim
 * ürettiklerimizde dolu. Platform tarafındaki metin ve önizleme `Ad`
 * kolonlarından (`headline`, `bodyText`, `ctaType`, `previewUrl`) geliyor.
 *
 * ⚠️ O kolonları senkron Faz 6'da DOLDURMAYA BAŞLADI; daha önce bağlanmış
 * hesaplarda ilk tam senkrona kadar boş kalabilirler. Boş metin bir hata
 * değil — arayüz reklamın adına düşüyor.
 */
export const CREATIVE_SIGNALS = [
  /// Sıklık tırmanıyor VE tıklama oranı düşüyor. Kreatif yorgunluğunun tanımı.
  'fatigue',
  /// Sıklık tek başına yüksek — henüz TO düşmemiş olabilir, erken uyarı.
  'high_frequency',
  /// TO belirgin düştü ama sıklık artmadı: sebep yorgunluk değil (rekabet,
  /// hedefleme, sezon). Ayrı bir sinyal olması bilinçli — aynı ada iki
  /// farklı müdahale gerekiyor.
  'ctr_decline',
  /// Platform reddetti. Metrik ne olursa olsun EN ÖNCELİKLİ satır.
  'disapproved',
  /// Öğrenme evresinde; sayılar henüz kararlı değil.
  'learning',
  /// Ölçüm için yeterli hacim yok. Bu sinyal varken istatistiksel
  /// sinyallerin HİÇBİRİ üretilmiyor.
  'low_volume',
  /// Hesap ortalamasının belirgin üstünde ROAS.
  'winner',
] as const;
export type CreativeSignalKey = (typeof CREATIVE_SIGNALS)[number];

export interface CreativeSignal {
  key: CreativeSignalKey;
  severity: 'info' | 'warn' | 'critical';
  /**
   * Sinyali doğuran ÖLÇÜM. Arayüz bunu çeviri metnine yerleştiriyor
   * ("sıklık 2,4 → 4,2").
   *
   * `null` = sinyal ölçümden değil durumdan geliyor (`disapproved`).
   */
  measure: {
    metric: string;
    recent: number | null;
    prior: number | null;
    /// Yüzde değişim. `prior` 0 ya da null ise `null`.
    changePct: number | null;
  } | null;
}

export interface CreativeDiagnosis {
  /**
   * 0–100 yorgunluk puanı. Yüksek = daha yorgun.
   *
   * `null` = hesaplanamadı (hacim yetersiz ya da trend penceresi aralığa
   * sığmadı). 🔴 `0` DEĞİL: sıfır "ölçtüm, yorgun değil" demek ve ikisini
   * aynı sayıya indirmek, veri olmayan reklamı "sağlıklı" ilan ederdi.
   */
  fatigueScore: number | null;
  signals: CreativeSignal[];
}

export interface CreativeAnalysisRow {
  adId: string;
  adName: string;
  adStatus: string;
  adSetId: string;
  adSetName: string;
  campaignId: string;
  campaignName: string;
  platform: string;
  accountName: string;
  currency: string;

  /// Platformdaki kreatifin metni. Senkron doldurana kadar `null`.
  headline: string | null;
  bodyText: string | null;
  ctaType: string | null;
  /// Platformun kendi önizleme bağlantısı (Meta `preview_shareable_link`).
  previewUrl: string | null;

  /// Bizim stüdyoda üretilmiş kreatif — yoksa `null`.
  creative: {
    id: string;
    type: CreativeType;
    status: CreativeStatus;
    name: string | null;
    /// İmzalı önizleme URL'i. Süreli; arayüz saklamamalı.
    thumbnailUrl: string | null;
    width: number | null;
    height: number | null;
  } | null;

  metrics: {
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    revenue: number;
    ctr: number | null;
    cpc: number | null;
    cpa: number | null;
    roas: number | null;
  };

  diagnosis: CreativeDiagnosis;

  /**
   * "AI ile İyileştir" bu reklam için çalışır mı.
   *
   * 🔴 `false` olmasının sebebi ÖNEMLİ ve arayüze taşınıyor: iyileştirme
   * kaynak bir GÖRSEL istiyor ve platformdan gelen bir reklamın bizde
   * görseli yok. Düğmeyi gösterip tıklandığında hata vermek, kullanıcıyı
   * ürünün yapamadığı bir şeye davet etmek olurdu.
   */
  improve: {
    available: boolean;
    /// `available: false` iken sebep anahtarı: `no_source_creative` |
    /// `source_not_ready` | `unsupported_type`.
    reason: string | null;
    /// Kaynak kreatif kimliği — `POST /creative/improve` bunu bekliyor.
    sourceCreativeId: string | null;
  };
}

export const creativeAnalysisSortSchema = z.enum([
  'spend',
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'cpa',
  'roas',
  'conversions',
  'revenue',
  /// Teşhis puanına göre: "en yorgun kreatif üstte". Sıralama SUNUCUDA,
  /// çünkü puan sunucuda hesaplanıyor ve arayüzün ikinci bir formülü olamaz.
  'fatigueScore',
]);

export const creativeAnalysisQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  campaignIds: z.string().optional(),
  adSetIds: z.string().optional(),
  platforms: z.string().optional(),
  status: z.string().optional(),
  q: z.string().trim().max(120).optional(),
  minSpend: z.coerce.number().min(0).optional(),
  /**
   * Trend penceresi (gün). Son N gün, ondan önceki N güne karşı.
   *
   * Varsayılan 7: haftanın günü etkisini (hafta sonu düşüşü) iki pencerede
   * de eşitliyor. 3 günlük bir pencere pazartesi ile cumartesiyi
   * karşılaştırıp her hafta sahte bir "yorgunluk" üretirdi.
   */
  trendDays: z.coerce.number().int().min(3).max(30).default(7),
  sort: creativeAnalysisSortSchema.default('spend'),
  dir: sortDirSchema,
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type CreativeAnalysisQuery = z.infer<typeof creativeAnalysisQuerySchema>;

export interface CreativeAnalysisResponse {
  rows: CreativeAnalysisRow[];
  range: { from: string; to: string };
  /// Trend hangi iki pencereden hesaplandı. `null` = aralık iki pencereyi
  /// almıyor; o zaman hiçbir satırda trend sinyali yok.
  trendWindows: {
    recent: { from: string; to: string };
    prior: { from: string; to: string };
  } | null;
  currency: string | null;
  /// Dönüşüm ve gelir hangi atıf kuralıyla sayıldı (Faz 6).
  attribution: {
    window: string;
    model: string;
    applied: boolean;
    appliedPlatforms: string[];
    unsupportedPlatforms: string[];
    modelApplied: boolean;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Marka logo kütüphanesi — 19 Ağu 2026
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Çalışma alanına kayıtlı bir logo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bir kütüphane, neden her üretimde yeniden yükleme değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Logo, kreatif üretiminde tekrarlanan TEK sabit girdi: ürün fotoğrafı her
 * kampanyada değişiyor, metin her postta değişiyor, logo değişmiyor. Onu her
 * seferinde yeniden yükletmek, kullanıcıya kendi dosya sisteminde aynı dosyayı
 * yeniden aratmak demekti — ve sihirbazın beş adımının en can sıkıcısı o adım
 * olurdu.
 *
 * ---------------------------------------------------------------------------
 * 🔴 `previewUrl` İMZALI ve KISA ÖMÜRLÜ
 * ---------------------------------------------------------------------------
 * Bucket public değil (`creative-storage.service.ts`). Liste her açıldığında
 * yeniden imzalanıyor; bir logonun adresini kopyalayıp paylaşmak, ömrü dolan
 * bir bağlantı paylaşmak demek.
 *
 * ⚠️ `s3Key` de dönüyor çünkü üretim isteği ONU taşıyor: sihirbaz kütüphaneden
 * bir logo seçtiğinde `logoAssetKey` alanına bu anahtar giriyor. Anahtar bir
 * sır değil (kiracı öneki kullanıcının kendi çalışma alanı) ve sunucu her
 * kullanımda sahipliğini ayrıca doğruluyor (`isOwnObjectKey`).
 */
export interface BrandLogoRow {
  id: string;
  name: string;
  s3Key: string;
  /// Kullanıcının yüklediği dosyanın adı — listede tanıdığı ad görünsün diye.
  /// SVG yüklendiyse burada `.svg` yazıyor, depoda duran nesne ise PNG.
  originalName: string | null;
  previewUrl: string | null;
  createdAt: string;
}

/**
 * Kütüphaneye ekleme.
 *
 * `assetKey` ZATEN YÜKLENMİŞ bir nesneyi gösteriyor: sihirbaz logoyu önce
 * `/creatives/uploads` biletiyle depoya koyuyor, kütüphaneye ekleme ayrı ve
 * SONRAKİ bir karar ("bu logo kütüphanenize eklensin mi?"). İkisini tek adımda
 * birleştirmek, kütüphaneye eklemek istemeyen kullanıcının logosunu da oraya
 * yazmak olurdu.
 */
export const brandLogoCreateSchema = z.object({
  assetKey: z.string().min(1).max(400),
  name: z.string().trim().min(1).max(80),
  originalName: z.string().trim().max(200).optional(),
});
export type BrandLogoCreateInput = z.infer<typeof brandLogoCreateSchema>;

/**
 * Kütüphanede tutulabilecek logo sayısı.
 *
 * Sınırın sebebi depolama değil ARAYÜZ: sihirbazın logo adımı listeyi tek
 * ekranda gösteriyor ve kaydırma gerektiren bir liste, "hangisi benim ana
 * logom" sorusunu her üretimde yeniden sordurur. Yirmi, bir markanın alt
 * markaları ve kampanya kilitleri için fazlasıyla yeterli.
 */
export const BRAND_LOGO_MAX = 20;
